/**
 * Shared client ↔ server clock offset for game timers.
 * remaining = finish_round_time − (Date.now() + offset)
 */

let clockOffsetMs = 0
let hasSynced = false

/** Parse ISO / Date / unix ms into epoch ms. */
export function parseServerNowMs(serverNow: unknown): number | null {
	if (serverNow == null || serverNow === "") return null
	if (typeof serverNow === "number" && Number.isFinite(serverNow)) {
		// Heuristic: seconds vs milliseconds
		return serverNow < 1e12 ? serverNow * 1000 : serverNow
	}
	if (serverNow instanceof Date) {
		const t = serverNow.getTime()
		return Number.isFinite(t) ? t : null
	}
	if (typeof serverNow === "string") {
		const t = Date.parse(serverNow)
		return Number.isFinite(t) ? t : null
	}
	return null
}

/**
 * Update offset from a server timestamp observed at `receivedAtMs` (defaults to now).
 * Simple one-way sync (no RTT/2); good enough for phase timers of tens of seconds.
 */
export function setServerClockOffset(serverNow: unknown, receivedAtMs: number = Date.now()): boolean {
	const serverMs = parseServerNowMs(serverNow)
	if (serverMs == null) return false
	clockOffsetMs = serverMs - receivedAtMs
	hasSynced = true
	return true
}

/** Prefer root `server_now`, then nested `timer.server_now`. */
export function syncServerClockFromPayload(payload: unknown): boolean {
	if (!payload || typeof payload !== "object") return false
	const root = payload as { server_now?: unknown; timer?: { server_now?: unknown } | null }
	if (setServerClockOffset(root.server_now)) return true
	if (root.timer && typeof root.timer === "object") {
		return setServerClockOffset(root.timer.server_now)
	}
	return false
}

export function getClockOffsetMs(): number {
	return clockOffsetMs
}

export function hasServerClockSync(): boolean {
	return hasSynced
}

/** Wall clock aligned to last known server time. */
export function getServerNowMs(): number {
	return Date.now() + clockOffsetMs
}

export function resetServerClockOffset(): void {
	clockOffsetMs = 0
	hasSynced = false
}
